import React, { Children } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { FlaskConical, Truck, ShieldCheck, Star, Sparkles as SparkleIcon } from 'lucide-react';
import { CTA } from './CTA';
import { Vial } from './Vial';
import { UKFlag } from './UKFlag';
const ENTRANCE_EASE = [0.22, 1, 0.36, 1] as const;
const PILLARS = [{
  icon: FlaskConical,
  title: 'Third-party tested',
  body: 'HPLC verified every batch'
}, {
  icon: Truck,
  title: 'Same-day UK dispatch',
  body: 'Tracked cold-chain delivery'
}, {
  icon: ShieldCheck,
  title: '30-day guarantee',
  body: 'Re-shipped if compromised'
}];
/* Sparkle — primary-green / white star that rises from the vial like
   magical particles. Each sparkle drifts upward while fading in,
   reaching peak brightness mid-flight, then fading and shrinking. */
type SparkleProps = {
  top?: string | number;
  left?: string | number;
  right?: string | number;
  bottom?: string | number;
  size?: number;
  delay?: number;
  duration?: number;
  color?: 'gold' | 'white';
  /** How far the sparkle rises before fading out (px). */
  drift?: number;
};
function Sparkle({
  top,
  left,
  right,
  bottom,
  size = 16,
  delay = 0,
  duration = 3.2,
  color = 'gold',
  drift = 90
}: SparkleProps) {
  const fill = color === 'white' ? '#FFFFFF' : '#FFC83D';
  return <motion.div aria-hidden className="absolute pointer-events-none" style={{
    top,
    left,
    right,
    bottom,
    width: size,
    height: size
  }} initial={{
    y: 0,
    opacity: 0,
    scale: 0.4,
    rotate: 0
  }} animate={{
    y: -drift,
    opacity: [0, 1, 1, 0],
    scale: [0.4, 1, 1.05, 0.6],
    rotate: [0, 90, 180]
  }} transition={{
    duration,
    repeat: Infinity,
    delay,
    ease: 'easeOut',
    times: [0, 0.2, 0.65, 1]
  }} data-id="element-289">
      <svg viewBox="0 0 32 32" width={size} height={size} style={{
      display: 'block',
      overflow: 'visible',
      filter: color === 'white' ? 'drop-shadow(0 0 4px rgba(255,255,255,0.8))' : 'drop-shadow(0 0 6px rgba(255,200,60,0.75))'
    }} data-id="element-290">
        <path d="M16 0
             C16 8, 20 12, 32 16
             C20 20, 16 24, 16 32
             C16 24, 12 20, 0 16
             C12 12, 16 8, 16 0 Z" fill={fill} data-id="element-291" />
      </svg>
    </motion.div>;
}
/* Positions emerge from the LOWER HALF of the vial area and rise upward.
   Mix of gold (default) and white sparkles for warm magical highlights. */
const SPARKLES: SparkleProps[] = [{
  top: '70%',
  left: '30%',
  size: 14,
  delay: 0.0,
  duration: 3.4,
  drift: 110
}, {
  top: '72%',
  right: '32%',
  size: 16,
  delay: 0.3,
  duration: 3.6,
  drift: 120
}, {
  top: '55%',
  left: '24%',
  size: 12,
  delay: 0.8,
  duration: 3.2,
  drift: 90
}, {
  top: '58%',
  right: '24%',
  size: 13,
  delay: 1.1,
  duration: 3.5,
  drift: 100
}, {
  top: '40%',
  left: '48%',
  size: 10,
  delay: 1.4,
  duration: 3.0,
  drift: 60,
  color: 'white'
}, {
  top: '78%',
  left: '42%',
  size: 9,
  delay: 1.8,
  duration: 3.3,
  drift: 95
}, {
  top: '50%',
  left: '64%',
  size: 11,
  delay: 2.1,
  duration: 3.4,
  drift: 85
}, {
  top: '60%',
  left: '36%',
  size: 8,
  delay: 2.5,
  duration: 3.0,
  drift: 75,
  color: 'white'
}, {
  top: '65%',
  right: '28%',
  size: 10,
  delay: 2.8,
  duration: 3.7,
  drift: 105
}, {
  top: '80%',
  left: '54%',
  size: 7,
  delay: 0.6,
  duration: 2.8,
  drift: 70
}, {
  top: '52%',
  left: '52%',
  size: 9,
  delay: 3.0,
  duration: 3.2,
  drift: 80,
  color: 'white'
}, {
  top: '68%',
  left: '38%',
  size: 15,
  delay: 1.6,
  duration: 3.8,
  drift: 115
}];
export function Hero() {
  const reduce = useReducedMotion();
  // Stagger every text element in the left column for a single orchestrated reveal.
  const copyContainer = {
    hidden: {},
    visible: {
      transition: reduce ? {} : {
        staggerChildren: 0.07,
        delayChildren: 0.05
      }
    }
  };
  const copyItem = {
    hidden: reduce ? {
      opacity: 0
    } : {
      opacity: 0,
      y: 14
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.55,
        ease: ENTRANCE_EASE
      }
    }
  };
  return <section className="relative w-full overflow-hidden" data-id="element-292">
      <div className="container mx-auto pt-14 lg:pt-20 pb-20 lg:pb-28" data-id="element-293">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-12 items-center" data-id="element-294">
          {/* Copy — staggered children */}
          <motion.div className="lg:col-span-7" variants={copyContainer} initial="hidden" animate="visible" data-id="element-295">
            {/* Eyebrow */}
            <motion.div variants={copyItem} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border bg-card mb-7" data-id="element-296">
              <UKFlag size={14} data-id="element-297" />
              <span className="text-[11px] font-medium tracking-wide uppercase text-muted-foreground" data-id="element-298">
                Shipped from the UK
                <span className="text-border mx-1.5" data-id="element-299">·</span>
                HPLC tested
              </span>
            </motion.div>

            {/* Headline */}
            <motion.h1 variants={copyItem} className="font-semibold tracking-[-0.035em] text-foreground text-[42px] leading-[1.02] sm:text-[56px] lg:text-[74px]" data-id="element-300">
              Lab-grade <span className="text-primary" data-id="element-301">peptides</span>
              <span className="text-primary" data-id="element-302">,</span>
              <br data-id="element-303" />
              <span className="text-foreground" data-id="element-304">purity in every </span>
              <span className="text-primary" data-id="element-305">.ml</span>
            </motion.h1>

            {/* Subhead */}
            <motion.p variants={copyItem} className="mt-6 max-w-[560px] text-[17px] leading-[1.55] text-muted-foreground" data-id="element-306">
              Every batch independently HPLC verified at{' '}
              <span className="font-mono text-foreground" data-id="element-307">≥99%</span> purity by
              an ISO 17025 accredited UK lab. Same-day dispatch, cold-chain,
              discreet packaging.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={copyItem} className="mt-9 flex flex-wrap items-center gap-3" data-id="element-308">
              <CTA to="/shop" variant="primary" size="lg" data-id="element-309">
                Shop peptides
              </CTA>
              <CTA href="#lab" variant="secondary" size="lg" data-id="element-310">
                View lab results
              </CTA>
            </motion.div>

            {/* Trustpilot indicator */}
            <motion.div variants={copyItem} className="mt-8 flex items-center gap-3" data-id="element-311">
              <div className="flex items-center gap-0.5" aria-label="4.8 out of 5" data-id="element-312">
                {Array.from({
                length: 5
              }).map((_, i) => <motion.span key={i} initial={reduce ? {
                opacity: 0
              } : {
                opacity: 0,
                scale: 0.4
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.35,
                delay: 0.55 + i * 0.06,
                ease: ENTRANCE_EASE
              }} data-id="element-313">
                    <Star className="h-4 w-4 fill-primary text-primary" strokeWidth={0} data-id="element-314" />
                  </motion.span>)}
              </div>
              <span className="text-[13px] text-muted-foreground" data-id="element-315">
                <span className="font-mono text-foreground" data-id="element-316">4.8</span>
                <span className="text-border mx-1.5" data-id="element-317">·</span>
                <span className="font-mono text-foreground" data-id="element-318">1,247</span> reviews
                on{' '}
                <span className="font-semibold text-foreground" data-id="element-319">
                  Trustpilot
                </span>
              </span>
            </motion.div>
          </motion.div>

          {/* Visual — composed reveal: backdrop → vial → annotations */}
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.4,
          delay: 0.1
        }} className="lg:col-span-5" data-id="element-320">
            <div className="relative w-full max-w-[460px] mx-auto" data-id="element-321">
              {/* Soft tonal backdrop — scales in subtly, with dot-grid texture overlay */}
              <motion.div initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.7,
              ease: ENTRANCE_EASE
            }} className="absolute inset-x-6 top-6 bottom-10 rounded-[28px] bg-secondary/60 origin-center overflow-hidden" data-id="element-322">
                {/* Dot-grid pattern — scientific texture */}
                <div aria-hidden className="absolute inset-0 opacity-50" style={{
                backgroundImage: 'radial-gradient(circle, hsl(var(--foreground) / 0.08) 1px, transparent 1.5px)',
                backgroundSize: '20px 20px'
              }} data-id="element-323" />
              </motion.div>

              {/* Inner card — with crosshair guides */}
              <motion.div initial={reduce ? {
              opacity: 0
            } : {
              opacity: 0,
              scale: 0.97
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.75,
              delay: 0.08,
              ease: ENTRANCE_EASE
            }} className="absolute inset-x-12 top-14 bottom-20 rounded-[22px] bg-card border border-border origin-center overflow-hidden" data-id="element-324">
                {/* Hairline crosshair — vertical */}
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-foreground/[0.06]" data-id="element-325" />
                {/* Hairline crosshair — horizontal */}
                <div className="absolute top-1/2 left-0 right-0 h-px bg-foreground/[0.06]" data-id="element-326" />
                {/* Tick marks */}
                <div className="absolute left-1/2 top-6 w-2 h-px bg-foreground/15 -translate-x-1/2" data-id="element-327" />
                <div className="absolute left-1/2 bottom-6 w-2 h-px bg-foreground/15 -translate-x-1/2" data-id="element-328" />
                <div className="absolute top-1/2 left-6 h-2 w-px bg-foreground/15 -translate-y-1/2" data-id="element-329" />
                <div className="absolute top-1/2 right-6 h-2 w-px bg-foreground/15 -translate-y-1/2" data-id="element-330" />
              </motion.div>

              {/* Vial */}
              <div className="relative flex items-end justify-center pt-12 pb-12" data-id="element-331">
                {/* Vial column — floating vial stacked on its own dynamic shadow */}
                <div className="relative flex flex-col items-center" data-id="element-332">
                  <motion.div animate={{
                  y: [0, -16, 0]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative text-foreground" data-id="element-333">
                    <Vial label="BPC-157" size={340} showShadow={false} data-id="element-334" />
                  </motion.div>

                  {/* Light-aware ground shadow — broader + lighter when vial is up,
                   tighter + darker when vial returns to rest (correct physics). */}
                  <motion.div aria-hidden animate={{
                  scaleX: [0.85, 1.15, 0.85],
                  scaleY: [0.75, 1.05, 0.75],
                  opacity: [0.3, 0.1, 0.3]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="-mt-4 h-3 w-[210px] rounded-full bg-foreground origin-center" style={{
                  filter: 'blur(10px)'
                }} data-id="element-335" />
                </div>

                {/* Sparkle field — primary-green twinkles scattered around the
                 vial. Each sparkle has its own position, size, delay and
                 duration so they twinkle organically rather than in sync. */}
                <div className="pointer-events-none absolute inset-0 z-20" data-id="element-336">
                  {SPARKLES.map((s, i) => <Sparkle key={i} {...s} data-id="element-337" />)}
                </div>

                {/* Annotation: purity (top-right) — connector draws, card fades */}
                <div className="hidden sm:flex absolute top-8 right-0 lg:right-2 items-center gap-3" data-id="element-338">
                  <motion.span aria-hidden initial={reduce ? {
                  opacity: 0
                } : {
                  scaleX: 0,
                  opacity: 0
                }} animate={{
                  scaleX: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.5,
                  delay: 1.05,
                  ease: ENTRANCE_EASE
                }} className="hidden md:block h-px w-10 bg-foreground/25 origin-right" data-id="element-339" />
                  <motion.div initial={reduce ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  x: 14
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.55,
                  delay: 1.15,
                  ease: ENTRANCE_EASE
                }} className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-340">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-341">
                      Purity
                    </div>
                    <div className="font-mono text-base text-foreground font-medium leading-none mt-0.5" data-id="element-342">
                      99.7%
                    </div>
                  </motion.div>
                </div>

                {/* Annotation: batch (bottom-left) */}
                <div className="hidden sm:flex absolute bottom-14 left-0 lg:left-2 items-center gap-3" data-id="element-343">
                  <motion.div initial={reduce ? {
                  opacity: 0
                } : {
                  opacity: 0,
                  x: -14
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  duration: 0.55,
                  delay: 1.25,
                  ease: ENTRANCE_EASE
                }} className="rounded-lg bg-background border border-border px-3 py-2 shadow-sm" data-id="element-344">
                    <div className="text-[9px] uppercase tracking-wider text-muted-foreground font-medium" data-id="element-345">
                      Batch
                    </div>
                    <div className="font-mono text-[13px] text-foreground font-medium leading-none mt-0.5" data-id="element-346">
                      BP-24091
                    </div>
                  </motion.div>
                  <motion.span aria-hidden initial={reduce ? {
                  opacity: 0
                } : {
                  scaleX: 0,
                  opacity: 0
                }} animate={{
                  scaleX: 1,
                  opacity: 1
                }} transition={{
                  duration: 0.5,
                  delay: 1.35,
                  ease: ENTRANCE_EASE
                }} className="hidden md:block h-px w-10 bg-foreground/25 origin-left" data-id="element-347" />
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Scroll cue — gently invites the user down without nagging */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 1.5
      }} className="hidden lg:flex justify-center mt-16" aria-hidden data-id="element-348">
          <div className="flex flex-col items-center gap-2" data-id="element-349">
            <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-medium" data-id="element-350">
              Scroll
            </span>
            <motion.span animate={reduce ? undefined : {
            y: [0, 6, 0],
            opacity: [0.35, 0.85, 0.35]
          }} transition={reduce ? undefined : {
            duration: 2.2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="block h-6 w-px bg-foreground origin-top" data-id="element-351" />
          </div>
        </motion.div>
      </div>
    </section>;
}